import { formatCents, formatTermDays } from '../lib/format';
import { miningModeLabel } from '../lib/miningModes';
import type { PaymentStatusResponse } from '../lib/api';

/**
 * What the customer bought, on the page they land on after paying.
 *
 * Deliberately the same information as the Metered Mining activation email, in the web
 * design system rather than a port of the email's HTML — someone who reads the email and
 * someone who stays on the page should learn the same facts.
 *
 * ---------------------------------------------------------------------------
 * EVERY VALUE IS SERVER-DECIDED. Nothing here re-quotes, re-prices, or derives a figure:
 * the amount is what was charged, the term is what PowerPool returned, and the payout
 * address arrives already masked. The component formats; it does not compute.
 *
 * ---------------------------------------------------------------------------
 * START AND END ARE OMITTED UNTIL THEY EXIST. PowerPool owns the term and only reports it
 * once an allocation is real, so a paid-but-not-yet-running order shows no dates rather
 * than a guess. That is also why this card never says "mining" on its own — the page
 * decides that from `fulfilment`.
 */

/** One row. Wraps rather than scrolls, which is what keeps long values mobile-safe. */
function Row({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <dt className="mm-help">{label}</dt>
      <dd className="mm-numeric m-0 text-right text-sm font-medium" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

/**
 * A provider timestamp, in words.
 *
 * UTC and explicitly labelled: a rental term is a contractual window, and rendering it in
 * whatever timezone the browser happens to sit in would make two customers disagree about
 * when the same order ends.
 */
function formatMoment(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

export default function ActivationSummary({ status }: { status: PaymentStatusResponse }) {
  const { configuration, currency } = status;
  const startsAt = formatMoment(status.startsAt);
  const endsAt = formatMoment(status.endsAt);

  return (
    <section className="mm-panel-raised mt-8 px-5 py-5 sm:px-6" data-testid="activation-summary">
      <h2 className="mm-eyebrow">Your rental</h2>

      <dl className="mt-4 grid gap-2">
        {configuration && (
          <>
            <Row
              label="Mining power"
              value={`${configuration.hashrateTh} TH/s`}
              testId="summary-hashrate"
            />
            <Row
              label="Rental term"
              value={formatTermDays(configuration.termDays)}
              testId="summary-term"
            />
            <Row
              label="Mining mode"
              value={miningModeLabel(configuration.miningMode)}
              testId="summary-mode"
            />
          </>
        )}

        {/* Only once PowerPool has told us. Never computed from the order date. */}
        {startsAt && <Row label="Started" value={startsAt} testId="summary-started" />}
        {/* Past tense once the term is over: "Ends" beside a date in the past reads as
            though something is still scheduled. */}
        {endsAt && (
          <Row
            label={status.fulfilment === "ended" ? "Ended" : "Ends"}
            value={endsAt}
            testId="summary-ends"
          />
        )}

        {status.payoutAddress && (
          <Row
            label="Payout address"
            value={
              <span className="font-mono text-xs" data-testid="summary-payout">
                {status.payoutAddress}
              </span>
            }
          />
        )}
      </dl>

      <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--mm-border)' }}>
        <Row
          label="Total paid"
          value={
            <span className="text-lg font-semibold">
              {formatCents(status.totalCents, currency)}
            </span>
          }
          testId="summary-total"
        />
      </div>

      {/* Secondary metadata: what they quote to support, not what they came here to see. */}
      <p className="mm-help mt-4 border-t pt-3" style={{ borderColor: 'var(--mm-border)' }}>
        Order <span className="mm-numeric font-medium">{status.orderNumber}</span>
      </p>
    </section>
  );
}
